import { createTheme, PaletteMode, alpha } from '@mui/material/styles';

// Paleta y tokens portados de Minimal UI Kit (https://minimals.cc)
// Fuente: https://github.com/minimal-ui-kit/material-kit-react

const grey = {
  50: '#FCFDFD',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#1C252E',
  900: '#141A21',
};

const primaryColors = {
  lighter: '#D0ECFE',
  light: '#73BAFB',
  main: '#1877F2',
  dark: '#0C44AE',
  darker: '#042174',
  contrastText: '#FFFFFF',
};

const secondaryColors = {
  lighter: '#EFD6FF',
  light: '#C684FF',
  main: '#8E33FF',
  dark: '#5119B7',
  darker: '#27097A',
  contrastText: '#FFFFFF',
};

const infoColors = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
};

const successColors = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: '#22C55E',
  dark: '#118D57',
  darker: '#065E49',
  contrastText: '#FFFFFF',
};

const warningColors = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: '#1C252E',
};

const errorColors = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#FFFFFF',
};

// Sombra suave de tarjeta al estilo Minimal (usa el gris como base tanto en claro como oscuro)
const cardShadow = () =>
  `0 0 2px 0 ${alpha(grey[500], 0.2)}, 0 12px 24px -4px ${alpha(grey[500], 0.12)}`;

// Crear tema según modo (light/dark)
export const getTheme = (mode: PaletteMode) => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: primaryColors,
      secondary: secondaryColors,
      info: infoColors,
      success: successColors,
      warning: warningColors,
      error: errorColors,
      grey,
      divider: alpha(grey[500], 0.2),
      ...(isDark
        ? {
            background: {
              default: grey[900],
              paper: grey[800],
            },
            text: {
              primary: '#FFFFFF',
              secondary: grey[500],
              disabled: grey[600],
            },
            action: {
              hover: alpha(grey[500], 0.08),
              selected: alpha(grey[500], 0.16),
              focus: alpha(grey[500], 0.24),
              disabled: alpha(grey[500], 0.8),
              disabledBackground: alpha(grey[500], 0.24),
              active: grey[500],
            },
          }
        : {
            background: {
              default: grey[100],
              paper: '#FFFFFF',
            },
            text: {
              primary: grey[800],
              secondary: grey[600],
              disabled: grey[500],
            },
            action: {
              hover: alpha(grey[500], 0.08),
              selected: alpha(grey[500], 0.16),
              focus: alpha(grey[500], 0.24),
              disabled: alpha(grey[500], 0.8),
              disabledBackground: alpha(grey[500], 0.24),
              active: grey[600],
            },
          }),
    },
    typography: {
      fontFamily: '"DM Sans", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 800, fontSize: '2.5rem', lineHeight: 1.25 },
      h2: { fontWeight: 800, fontSize: '2rem', lineHeight: 1.33 },
      h3: { fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.5 },
      h4: { fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.5 },
      h5: { fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.5 },
      h6: { fontWeight: 600, fontSize: '1.0625rem', lineHeight: 1.56 },
      subtitle1: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
      subtitle2: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.57 },
      body1: { fontSize: '1rem', lineHeight: 1.5 },
      body2: { fontSize: '0.875rem', lineHeight: 1.57 },
      caption: { fontSize: '0.75rem', lineHeight: 1.5 },
      overline: { fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.5, textTransform: 'uppercase' },
      button: { fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.71, textTransform: 'none' },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8 },
          sizeLarge: { minHeight: 48 },
          containedInherit: {
            color: '#FFFFFF',
            backgroundColor: grey[800],
            '&:hover': { backgroundColor: grey[900] },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundImage: 'none',
            boxShadow: cardShadow(),
          },
        },
      },
      MuiCardHeader: {
        defaultProps: {
          titleTypographyProps: { variant: 'h6' },
          subheaderTypographyProps: { variant: 'body2' },
        },
        styleOverrides: {
          root: { padding: '24px 24px 0' },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: alpha(grey[500], isDark ? 0.24 : 0.16) },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8 },
          notchedOutline: { borderColor: alpha(grey[500], 0.2) },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: { '& .MuiOutlinedInput-root': { borderRadius: 8 } },
        },
      },
      MuiTable: {
        defaultProps: { size: 'small' },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: isDark ? grey[400] : grey[600],
            backgroundColor: isDark ? grey[900] : grey[200],
          },
          root: { borderBottomColor: alpha(grey[500], 0.2) },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { fontSize: '0.875rem' },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: 'none' },
        },
      },
    },
  });
};

// Compatibilidad: tema light por defecto
export const theme = getTheme('light');
export default theme;
